/**
 * Fitting guide panel (RAZ-328).
 *
 * Drives blocks/rb-fitting-guide.liquid on the support page. Liquid renders every guide as
 * a hidden panel and writes a lookup of which model, series and fitment point at which
 * guide. This component shows the panel pickGuide() chooses for the saved vehicle, and
 * follows the store, so a finder submit, a change in the header, a removal or another tab
 * all swap the guide in place. A vehicle with no guide gets the no-guide line instead, and
 * with no vehicle saved the whole block is hidden.
 *
 * A vehicle whose guide is a redirect gets its title and a button to the URL. Pressing the
 * finder in the same section goes straight there instead: the finder announces the press
 * with `vehicleFinder:submit`, and only that press travels. A vehicle set in the header,
 * in another tab or on an earlier visit shows the button and never moves the shopper.
 *
 * The first frame is not painted from here. The block's pre-paint script has already
 * un-hidden the right panel and written its title before Alpine boots, and this
 * component's bindings produce the same result, so taking over changes nothing on screen.
 *
 * It fetches nothing. The vehicle comes from the store and the guides from the page.
 */

import { fill } from '../utils/vehicleResolver.js';
import { pickGuide } from '../utils/fittingGuide.js';

/** Copy used when a merchant leaves a block setting blank. `{tokens}`: see labels(). */
export const DEFAULT_COPY = {
  titleTemplate: '{make} {model} fitting guide',
  missingText: "We don't have a guide for your {model} yet.",
};

/** A lookup with nothing in it: every vehicle gets no panel. */
const EMPTY_LOOKUP = { f: {}, s: {}, m: {}, guides: [] };

/** Announced by components/vehicleFinder.js when a stay-on-page finder saves a vehicle. */
const FINDER_SUBMIT_EVENT = 'vehicleFinder:submit';

/**
 * A merchant string, or the default when the setting is missing altogether. A setting the
 * merchant cleared stays blank, because that is what the pre-paint script paints too.
 * @param {*} value
 * @param {string} fallback
 * @returns {string}
 */
function copyOr(value, fallback) {
  return typeof value === 'string' ? value : fallback;
}

/**
 * The lookup as the block wrote it, or an empty one when it cannot be read.
 * @param {*} lookup a parsed object, or the raw JSON text
 * @returns {Object}
 */
function readLookup(lookup) {
  if (typeof lookup === 'string') {
    try {
      return readLookup(JSON.parse(lookup));
    } catch {
      return EMPTY_LOOKUP;
    }
  }
  return lookup && typeof lookup === 'object' && !Array.isArray(lookup) ? lookup : EMPTY_LOOKUP;
}

export default {
  name: 'fittingGuide',

  /**
   * @param {Object} [params]
   * @param {string} [params.titleTemplate] title when the vehicle has a guide
   * @param {string} [params.missingText] line for a vehicle with no guide yet
   * @param {boolean} [params.isDesignMode] true in the theme editor, where the block stays
   *   visible with no vehicle so a merchant can find and select it, and never redirects
   * @param {Function} [params.navigate] injected by the unit tests in place of location
   */
  component(params = {}) {
    return {
      lookup: EMPTY_LOOKUP,

      copy: {
        titleTemplate: copyOr(params.titleTemplate, DEFAULT_COPY.titleTemplate),
        missingText: copyOr(params.missingText, DEFAULT_COPY.missingText),
      },

      isDesignMode: params.isDesignMode === true,

      /** The window listener for finder presses, kept so destroy() can remove it. */
      finderListener: null,

      init() {
        this.applyLookup(this.$refs?.lookup?.textContent ?? null);
        this.$watch?.('activeHandle', () => this.pauseAll());
        this.finderListener = (event) => this.followFinder(event);
        window.addEventListener(FINDER_SUBMIT_EVENT, this.finderListener);
      },

      destroy() {
        if (this.finderListener) window.removeEventListener(FINDER_SUBMIT_EVENT, this.finderListener);
        this.finderListener = null;
      },

      /**
       * Adopt the block's lookup. This is the test seam: no DOM, no Alpine.
       * @param {Object|string|null} lookup
       */
      applyLookup(lookup) {
        this.lookup = readLookup(lookup);
      },

      /** The saved vehicle, or null. Reactive through the Alpine store. */
      get vehicle() {
        return this.$store?.vehicle?.vehicle ?? null;
      },

      get picked() {
        return pickGuide(this.lookup, this.vehicle);
      },

      /** The guide handle to show, or null for none. */
      get activeHandle() {
        return this.picked.handle;
      },

      /**
       * 'none' (no vehicle), 'match' (the vehicle has a guide on the page), 'redirect' (its
       * guide is a link) or 'missing'.
       */
      get state() {
        const { source, url } = this.picked;
        if (source === 'none' || source === 'missing') return source;
        return url ? 'redirect' : 'match';
      },

      /** The redirect for the saved vehicle's guide, or '' when it has none. */
      get redirectUrl() {
        return this.picked.url ?? '';
      },

      /** No vehicle, nothing to say: the block takes no room at all, except in the editor. */
      get isHidden() {
        return this.state === 'none' && !this.isDesignMode;
      },

      get title() {
        const hasGuide = this.state === 'match' || this.state === 'redirect';
        return hasGuide ? fill(this.copy.titleTemplate, this.vehicle) : '';
      },

      get missingMessage() {
        return fill(this.copy.missingText, this.vehicle);
      },

      /**
       * @param {string} handle
       * @returns {boolean}
       */
      isActive(handle) {
        return handle === this.activeHandle;
      },

      /**
       * Swap a guide's poster for its player. The poster is the button, so the video only
       * downloads once the shopper asks for it.
       * @param {Event} event
       */
      play(event) {
        const poster = event?.currentTarget;
        const video = poster?.closest?.('[data-guide]')?.querySelector?.('video');
        if (!poster || !video) return;
        poster.hidden = true;
        video.setAttribute('controls', '');
        // The focused poster has just gone, so hand focus to the player it revealed;
        // otherwise a keyboard or screen reader shopper lands back at the top of the page.
        video.focus?.({ preventScroll: true });
        const playing = video.play();
        if (playing && typeof playing.catch === 'function') playing.catch(() => {});
      },

      /**
       * A finder saved a vehicle. If it is the finder in this block's section and the
       * vehicle's guide is a redirect, go there; any other guide shows in place as usual.
       * @param {CustomEvent} event `vehicleFinder:submit`, dispatched from the finder
       */
      followFinder(event) {
        if (this.isDesignMode) return;
        const section = this.$root?.closest?.('.shopify-section');
        if (!section || !event?.target || !section.contains(event.target)) return;
        const { url } = pickGuide(this.lookup, event.detail?.vehicle ?? this.vehicle);
        if (url) this.navigate(url);
      },

      /**
       * Go to a redirect. Injectable so the unit tests can read the URL without a browser.
       * @param {string} href
       */
      navigate(href) {
        if (typeof params.navigate === 'function') {
          params.navigate(href);
          return;
        }
        window.location.assign(href);
      },

      /** Stop every guide's video, so one hidden by a vehicle change never plays on. */
      pauseAll() {
        const videos = this.$el?.querySelectorAll?.('video') ?? [];
        Array.from(videos).forEach((video) => video.pause());
      },
    };
  },
};
